import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { SilentRequest } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

export interface TokenInfo {
  token: string;
  scopes: string[];
  expiresOn: Date;
  claims: any;
}

export interface TestResult {
  success: boolean;
  statusCode?: number;
  message: string;
  data?: any;
  tokenInfo?: TokenInfo;
}

@Injectable({
  providedIn: 'root'
})
export class AuthTestingService {

  constructor(
    private msalService: MsalService,
    private http: HttpClient
  ) {}

  /**
   * Test the user delegation flow with specific scopes
   */
  testUserDelegationFlow(scopes: string[]): Observable<TestResult> {
    return new Observable(observer => {
      const account = this.msalService.instance.getActiveAccount();
      
      if (!account) {
        observer.next({
          success: false,
          message: 'No active account found. Please login first.'
        });
        observer.complete();
        return;
      }

      const silentRequest: SilentRequest = {
        scopes: scopes,
        account: account
      };

      this.msalService.acquireTokenSilent(silentRequest).subscribe({
        next: (result) => {
          const tokenInfo: TokenInfo = {
            token: result.accessToken,
            scopes: result.scopes || [],
            expiresOn: result.expiresOn || new Date(),
            claims: this.parseJwtClaims(result.accessToken)
          };

          observer.next({
            success: true,
            message: 'Token acquired successfully',
            tokenInfo: tokenInfo
          });
          observer.complete();
        },
        error: (error) => {
          observer.next({
            success: false,
            message: `Failed to acquire token: ${error.message || error}`,
            data: error
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Test API call with current token
   */
  testApiCall(endpoint: string, method: 'GET' | 'POST' = 'GET', body?: any): Observable<TestResult> {
    return new Observable(observer => {
      const account = this.msalService.instance.getActiveAccount();
      
      if (!account) {
        observer.next({
          success: false,
          message: 'No active account found. Please login first.'
        });
        observer.complete();
        return;
      }

      // Determine required scopes based on endpoint
      let requiredScopes: string[];
      if (endpoint.includes('Ticket')) {
        requiredScopes = [environment.azureAuthenticationConfig.scopes.helpdesk];
      } else {
        requiredScopes = [environment.azureAuthenticationConfig.scopes.userAccess];
      }

      const silentRequest: SilentRequest = {
        scopes: requiredScopes,
        account: account
      };

      this.msalService.acquireTokenSilent(silentRequest).subscribe({
        next: (tokenResult) => {
          const headers = new HttpHeaders({
            'Authorization': `Bearer ${tokenResult.accessToken}`,
            'Content-Type': 'application/json'
          });

          const url = `${environment.apiEndpoint}api/${endpoint}`;
          let request: Observable<any>;

          if (method === 'POST') {
            request = this.http.post(url, body || {}, { headers, observe: 'response' });
          } else {
            request = this.http.get(url, { headers, observe: 'response' });
          }

          request.subscribe({
            next: (response) => {
              observer.next({
                success: true,
                statusCode: response.status,
                message: `API call successful (${response.status})`,
                data: response.body,
                tokenInfo: {
                  token: tokenResult.accessToken,
                  scopes: tokenResult.scopes || [],
                  expiresOn: tokenResult.expiresOn || new Date(),
                  claims: this.parseJwtClaims(tokenResult.accessToken)
                }
              });
              observer.complete();
            },
            error: (error) => {
              observer.next({
                success: false,
                statusCode: error.status,
                message: `API call failed (${error.status}): ${error.error?.message || error.message}`,
                data: error.error
              });
              observer.complete();
            }
          });
        },
        error: (tokenError) => {
          observer.next({
            success: false,
            message: `Failed to acquire token for API call: ${tokenError.message || tokenError}`
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Test the problematic GetTicketPage endpoint
   */
  testGetTicketPage(): Observable<TestResult> {
    const testBody = {
      page: 0,
      pageSize: 10,
      keyword: null,
      skipUntil: null,
      skipIds: null,
      ticketStatusIDs: null,
      categoryIDs: null,
      assignedToTeamIDs: null,
      resolutionTypeID: null,
      acceptedBy: null,
      submittedDateStart: null,
      submittedDateEnd: null,
      acceptedDateStart: null,
      acceptedDateEnd: null,
      closedDateStart: null,
      closedDateEnd: null,
      isDescTicketNo: null,
      isDescAcceptedBy: null,
      isDescSubmittedBy: null,
      isDescClosedBy: null,
      isDescSubmittedDate: null,
      isDescAcceptedDate: null,
      isDescClosedDate: null
    };

    return this.testApiCall('Ticket/GetTicketPage', 'POST', testBody);
  }

  /**
   * Generate curl command for manual testing
   */
  generateCurlCommand(endpoint: string, method: 'GET' | 'POST' = 'GET', body?: any): Observable<string> {
    return new Observable(observer => {
      const account = this.msalService.instance.getActiveAccount();
      
      if (!account) {
        observer.next('# No active account found. Please login first.');
        observer.complete();
        return;
      }

      // Determine required scopes
      let requiredScopes: string[];
      if (endpoint.includes('Ticket')) {
        requiredScopes = [environment.azureAuthenticationConfig.scopes.helpdesk];
      } else {
        requiredScopes = [environment.azureAuthenticationConfig.scopes.userAccess];
      }

      const silentRequest: SilentRequest = {
        scopes: requiredScopes,
        account: account
      };

      this.msalService.acquireTokenSilent(silentRequest).subscribe({
        next: (result) => {
          const url = `${environment.apiEndpoint}api/${endpoint}`;
          let curlCommand = `curl -X '${method}' \\\n  '${url}' \\\n`;
          curlCommand += `  -H 'accept: */*' \\\n`;
          curlCommand += `  -H 'Content-Type: application/json' \\\n`;
          curlCommand += `  -H 'Authorization: Bearer ${result.accessToken}'`;

          if (method === 'POST' && body) {
            curlCommand += ` \\\n  -d '${JSON.stringify(body, null, 2)}'`;
          }

          observer.next(curlCommand);
          observer.complete();
        },
        error: (error) => {
          observer.next(`# Failed to acquire token: ${error.message || error}`);
          observer.complete();
        }
      });
    });
  }

  /**
   * Parse JWT token claims
   */
  private parseJwtClaims(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to parse JWT claims:', error);
      return {};
    }
  }

  /**
   * Get comprehensive authentication debug info
   */
  getAuthDebugInfo(): Observable<any> {
    return new Observable(observer => {
      const account = this.msalService.instance.getActiveAccount();
      const allAccounts = this.msalService.instance.getAllAccounts();

      const debugInfo: any = {
        activeAccount: account ? {
          username: account.username,
          name: account.name,
          localAccountId: account.localAccountId,
          environment: account.environment,
          tenantId: account.tenantId
        } : null,
        totalAccounts: allAccounts.length,
        environment: {
          clientId: environment.azureAuthenticationConfig.clientId,
          authority: environment.azureAuthenticationConfig.authority,
          redirectUri: environment.azureAuthenticationConfig.redirectUri,
          apiEndpoint: environment.apiEndpoint,
          availableScopes: environment.azureAuthenticationConfig.scopes
        }
      };

      if (account) {
        // Try to get current token info
        const silentRequest: SilentRequest = {
          scopes: environment.azureAuthenticationConfig.scopes.all,
          account: account
        };

        this.msalService.acquireTokenSilent(silentRequest).subscribe({
          next: (result) => {
            debugInfo.currentToken = {
              scopes: result.scopes,
              expiresOn: result.expiresOn,
              claims: this.parseJwtClaims(result.accessToken)
            };
            observer.next(debugInfo);
            observer.complete();
          },
          error: (error) => {
            debugInfo.tokenError = error.message || error;
            observer.next(debugInfo);
            observer.complete();
          }
        });
      } else {
        observer.next(debugInfo);
        observer.complete();
      }
    });
  }
}